import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, PhoneCall, TrendingUp, Users } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/overview`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Calls',
      value: stats?.total_calls || 0,
      icon: PhoneCall,
      color: 'bg-blue-50 text-blue-600',
      borderColor: 'border-blue-200',
    },
    {
      title: 'Assessments',
      value: stats?.total_assessments || 0,
      icon: FileText,
      color: 'bg-emerald-50 text-emerald-600',
      borderColor: 'border-emerald-200',
    },
    {
      title: 'Avg Score',
      value: `${stats?.average_score || 0}%`,
      icon: TrendingUp,
      color: 'bg-indigo-50 text-indigo-600',
      borderColor: 'border-indigo-200',
    },
  ];

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.name}
          </h1>
          <p className="text-slate-600">Manage SOPs, review analytics, and oversee call quality</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className={`p-6 border ${stat.borderColor} shadow-sm`}
                data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              onClick={() => navigate('/sops')}
              className="bg-[#4338CA] hover:bg-[#3730A3] text-white h-auto py-4"
              data-testid="manage-sops-button"
            >
              <FileText className="mr-2" size={20} />
              Manage SOPs
            </Button>
            <Button
              onClick={() => navigate('/upload')}
              variant="outline"
              className="border-slate-300 h-auto py-4"
              data-testid="upload-call-button"
            >
              <PhoneCall className="mr-2" size={20} />
              Upload Call
            </Button>
            <Button
              onClick={() => navigate('/analytics')}
              variant="outline"
              className="border-slate-300 h-auto py-4"
              data-testid="view-analytics-button"
            >
              <TrendingUp className="mr-2" size={20} />
              View Analytics
            </Button>
          </div>
        </Card>

        {/* Recent Assessments */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recent Assessments
          </h2>
          {loading ? (
            <p className="text-slate-600">Loading...</p>
          ) : stats?.recent_assessments?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_assessments.slice(0, 5).map((assessment) => (
                <div
                  key={assessment.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`recent-assessment-${assessment.id}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Call ID: {assessment.call_id.slice(0, 8)}...</p>
                    <p className="text-sm text-slate-600">
                      {new Date(assessment.assessed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">{assessment.overall_score}%</p>
                      <p className="text-xs text-slate-600">Score</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/assessment/${assessment.call_id}`)}
                      data-testid={`view-assessment-${assessment.id}`}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 mb-4">No assessments yet</p>
              <Button onClick={() => navigate('/upload')} data-testid="start-assessment-button">
                Upload First Call
              </Button>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;